const SOURCE_PARAM = 'source'
const STORAGE_KEY = 'providersDirectoryAccessSource'

let cachedSource = null
let hasResolved = false

const resolveSource = () => {
  let sourceFromUrl = null

  try {
    sourceFromUrl = new URLSearchParams(window.location.search).get(
      SOURCE_PARAM
    )
  } catch (error) {
    sourceFromUrl = null
  }

  try {
    if (sourceFromUrl) {
      window.sessionStorage.setItem(STORAGE_KEY, sourceFromUrl)
      return sourceFromUrl
    }

    return window.sessionStorage.getItem(STORAGE_KEY) || null
  } catch (error) {
    // Safari in private mode throws on any sessionStorage access. The token
    // still works for this page load, it just will not survive a reload.
    return sourceFromUrl
  }
}

/**
 * Read the access token that unlocks restricted directory states.
 *
 * The token arrives once as a `source` URL param and has to survive every later
 * navigation, including the ones that rebuild the query string from Redux state.
 * It is kept in sessionStorage rather than Redux so a full page reload does not
 * drop it - provider detail pages are reachable by direct link - and so it stays
 * scoped to the one tab.
 *
 * Resolved once per page load. Re-reading the URL later would lose a token that
 * a rebuilt query string happened to drop.
 *
 * @returns {string|null} The token, or null when the member has none
 */
export const getDirectoryAccessSource = () => {
  if (!hasResolved) {
    cachedSource = resolveSource()
    hasResolved = true
  }

  return cachedSource
}

/**
 * Token as a params object, for spreading into a URLSearchParams payload.
 * Empty when there is no token, so ordinary requests are unchanged.
 * @returns {Object} `{ source }` or `{}`
 */
export const directoryAccessParams = () => {
  const source = getDirectoryAccessSource()

  return source ? { [SOURCE_PARAM]: source } : {}
}

/**
 * Append the token to a URL that is not built from a params object.
 * @param {string} url - Path, with or without an existing query string
 * @returns {string} The url, unchanged when there is no token
 */
export const appendDirectoryAccessSource = url => {
  const source = getDirectoryAccessSource()

  if (!source) {
    return url
  }

  const separator = url.includes('?') ? '&' : '?'

  return `${url}${separator}${SOURCE_PARAM}=${encodeURIComponent(source)}`
}
